import { Dictionary, noop } from 'lodash';
import { Component, DefineComponent, markRaw, reactive } from 'vue';
import LoadingService from '@/dossier/services/LoadingService';
import { ModalClosed } from '@/shared/utils/errors';
import viewEvents, { EscapePressed } from '@/shared/view/viewEvents';

export type ModalParams = Dictionary<any>;
export const MODAL_CONFIRMED = Symbol('modal confirmed');
export const MODAL_DENIED = Symbol('modal denied'); // TODO @Seppe not a general concern
export const MODAL_CANCELLED = Symbol('modal cancelled');
export type BasicModalResult = typeof MODAL_CONFIRMED | typeof MODAL_DENIED | typeof MODAL_CANCELLED;

export interface OnModalClose<Result = any> {
  (result: Result): void;
}

export type ModalComponent = Component | DefineComponent | (() => Promise<Component | DefineComponent>);

export class ModalStackEntry {
  constructor(
    readonly component: Component,
    readonly params: ModalParams,
    readonly onClose: OnModalClose,
  ) {
  }

  close(result: any) {
    this.onClose(result);
  }
}

class Modal {
  stack: Array<ModalStackEntry> = reactive([]);

  constructor() {
    viewEvents.on(EscapePressed, e => {
      if (this.stack.length > 0) {
        this.close(MODAL_CANCELLED);
        e.handled = true;
      }
    }, 500);
  }

  async open<Result>(component: ModalComponent, params: ModalParams = {}, onClose: OnModalClose<Result> = noop): Promise<void> {
    try {
      LoadingService.start();
      const componentResolved = await this.resolveComponent(component);
      this.stack.push(markRaw(new ModalStackEntry(componentResolved, params, onClose)));
    } finally {
      await LoadingService.stop();
    }
  }

  async openImmediate<Result>(component: ModalComponent, params: ModalParams = {}, onClose: OnModalClose<Result> = noop): Promise<void> {
    try {
      const componentResolved = await this.resolveComponent(component);
      this.stack.push(markRaw(new ModalStackEntry(componentResolved, params, onClose)));
    } catch {
      console.warn('Failed to load modal component', component);
    }
  }

  openAndWait<Result = any>(component: ModalComponent, params: ModalParams = {}): Promise<Result> {
    return new Promise<Result>((resolve, reject) => {
      this.open<Result>(component, params, result => {
        if (result === MODAL_CANCELLED) {
          reject(new ModalClosed());
        } else {
          resolve(result);
        }
      }).catch(reject);
    });
  }

  close(result?: any): void {
    this.stack.pop()?.close(result);
  }

  closeAll(): void {
    this.stack.forEach(e => e.close(MODAL_CANCELLED));
    this.stack.splice(0);
  }

  private async resolveComponent(component: ModalComponent): Promise<Component> {
    if (typeof component !== 'function' || 'displayName' in component) return component;
    // eslint-disable-next-line @typescript-eslint/ban-types
    const module = await (component as Function)();
    return module.default;
  }
}

export default new Modal();